// „Neuer Eigentümer“ speichern: eigentuemer anlegen, Kontaktkanäle, Objektverknüpfung,
// Einheiten (je Einheit eine neue Zeile; beim Wechsel enden die Vorbesitzer am Vortag des
// Stichtags), Ordnerwechsel und Drive-Ordner. Nur additiv bzw. gezielt je Zeile –
// nie „alles löschen, neu anlegen“.

import { supabase } from '../supabase/client';
import { speichereKanaele, type KontaktKanal } from '../kontakt/kontaktKanaele';
import { heuteIso } from '../immobilien/format';
import { fuehreWechselAus, legeEigentuemerOrdnerAn, planeEigentuemerEinheitWechsel, planeEigentuemerWechsel } from '../wechsel/api';
import { setzeObjektEigentuemer } from './verknuepfung';
import { eigentuemerPayload, type NeuerEigentuemerEntwurf, type SchreibPlan } from './neuerEigentuemer';
import type { RolleKurz } from './rollen';

export interface EigAnlegenErgebnis {
  id: string;
  /** Teilschritte, die nicht geklappt haben (Eigentümer ist trotzdem angelegt) */
  warnungen: string[];
}

const meldung = (e: unknown): string => (e instanceof Error ? e.message : 'Fehler');

function kanaeleAus(e: Pick<NeuerEigentuemerEntwurf, 'email' | 'telefon'>): KontaktKanal[] {
  const k: KontaktKanal[] = [];
  if (e.email.trim()) k.push({ kanal: 'email', wert: e.email.trim(), label: null, ist_primaer: true, sortierung: 0 });
  if (e.telefon.trim()) k.push({ kanal: 'telefon', wert: e.telefon.trim(), label: null, ist_primaer: true, sortierung: 0 });
  return k;
}

export async function legeEigentuemerAn(e: NeuerEigentuemerEntwurf, plan: SchreibPlan, rollen: readonly RolleKurz[]): Promise<EigAnlegenErgebnis> {
  const { data, error } = await supabase.from('eigentuemer').insert(eigentuemerPayload(e, rollen)).select('id').single();
  if (error) throw new Error(error.message);
  const id = (data as { id: string }).id;
  const warnungen: string[] = [];
  try { await speichereKanaele(supabase, 'eigentuemer', id, kanaeleAus(e)); }
  catch (err) { warnungen.push(`E-Mail/Telefon nicht gespeichert: ${meldung(err)}`); }
  warnungen.push(...await schreibeZuordnung(id, e, plan));
  return { id, warnungen };
}

/**
 * Verknüpft einen (neuen oder vorhandenen) Eigentümer mit Objekt bzw. Einheiten laut Plan –
 * „Neuer Eigentümer“ und „Objekt verknüpfen“ im Panel „Objekte & Rollen“. Liefert Warnungen.
 */
export async function schreibeZuordnung(id: string, e: Pick<NeuerEigentuemerEntwurf, 'beginn' | 'ende'>, plan: SchreibPlan): Promise<string[]> {
  const warnungen: string[] = [];
  const heute = heuteIso();
  const { data: { user } } = await supabase.auth.getUser();
  const angelegtVon = user?.id ?? null;

  // Objektverknüpfung (objekt_eigentuemer) – auch bei Einheiten, die KI liest diese Tabelle.
  // Eine früher beendete Verknüpfung wird wieder geöffnet (vertrag_bis laut Plan).
  try {
    await setzeObjektEigentuemer(plan.objektId, id, plan.objektLink.vertragAb);
    const { error: bisFehler } = await supabase.from('objekt_eigentuemer').update({ vertrag_bis: plan.objektLink.vertragBis })
      .eq('objekt_id', plan.objektId).eq('eigentuemer_id', id);
    if (bisFehler) warnungen.push(`Vertragsende nicht gespeichert: ${bisFehler.message}`);
  } catch (err) {
    warnungen.push(`Objekt nicht verknüpft: ${meldung(err)}`);
  }

  const wechselIds: string[] = [];
  if (plan.ebene === 'objekt' && plan.objektWechselVon.length > 0) {
    try {
      wechselIds.push(...await planeEigentuemerWechsel({ objektId: plan.objektId, altRefIds: plan.objektWechselVon, neuRefId: id, faelligAm: e.beginn, angelegtVon }));
    } catch (err) {
      warnungen.push(`Eigentümerwechsel nicht vorgemerkt: ${meldung(err)}`);
    }
  }

  // Wechsel: MEA, Hausgeld und Rücklage gehören zur Einheit und gehen auf den neuen Eigentümer über –
  // die Werte der Vorbesitzer in einer Abfrage statt je Einheit
  type Werte = { mea: unknown; hausgeld_vorschuss: unknown; ruecklage_vorschuss: unknown };
  const mitWerten = plan.einheiten.filter(x => x.werteVon);
  const vorwerte = new Map<string, Werte>();
  if (mitWerten.length > 0) {
    const { data } = await supabase.from('einheit_eigentuemer').select('einheit_id, eigentuemer_id, mea, hausgeld_vorschuss, ruecklage_vorschuss')
      .in('einheit_id', mitWerten.map(x => x.einheitId)).in('eigentuemer_id', [...new Set(mitWerten.map(x => x.werteVon as string))]);
    for (const r of (data ?? []) as (Werte & { einheit_id: string; eigentuemer_id: string })[]) {
      vorwerte.set(`${r.einheit_id}|${r.eigentuemer_id}`, { mea: r.mea, hausgeld_vorschuss: r.hausgeld_vorschuss, ruecklage_vorschuss: r.ruecklage_vorschuss });
    }
  }

  for (const x of plan.einheiten) {
    const werte: Werte = (x.werteVon && vorwerte.get(`${x.einheitId}|${x.werteVon}`)) || { mea: null, hausgeld_vorschuss: null, ruecklage_vorschuss: null };
    // Upsert: eine frühere (beendete) Zeile desselben Eigentümers wird wieder aktiv
    const { error: insFehler } = await supabase.from('einheit_eigentuemer').upsert({
      einheit_id: x.einheitId, eigentuemer_id: id, ...werte,
      vertrag_ab: e.beginn, vertrag_bis: e.ende || null, sev_status: plan.sevStatus, updated_at: new Date().toISOString(),
    }, { onConflict: 'einheit_id,eigentuemer_id' });
    if (insFehler) { warnungen.push(`Einheit nicht zugeordnet: ${insFehler.message}`); continue; }

    for (const alt of x.vorbesitzerIds) {
      // Vorbesitzer endet am Vortag des Stichtags – gleich gesetzt, damit Liste und Konto es sofort zeigen
      const { error: endFehler } = await supabase.from('einheit_eigentuemer').update({ vertrag_bis: plan.vorbesitzerBis, updated_at: new Date().toISOString() })
        .eq('einheit_id', x.einheitId).eq('eigentuemer_id', alt).is('vertrag_bis', null);
      if (endFehler) warnungen.push(`Vorbesitzer nicht beendet: ${endFehler.message}`);
      try {
        wechselIds.push(await planeEigentuemerEinheitWechsel({ objektId: plan.objektId, einheitId: x.einheitId, altRefId: alt, neuRefId: id, faelligAm: e.beginn, angelegtVon }));
      } catch (err) {
        warnungen.push(`Ordnerwechsel nicht vorgemerkt: ${meldung(err)}`);
      }
    }
  }

  // Vorbesitzer ohne verbleibende Einheit: Objektverknüpfung endet am Vortag des Stichtags
  for (const alt of plan.objektLinkEndet) {
    const { error: endFehler } = await supabase.from('objekt_eigentuemer').update({ vertrag_bis: plan.vorbesitzerBis })
      .eq('objekt_id', plan.objektId).eq('eigentuemer_id', alt).is('vertrag_bis', null);
    if (endFehler) warnungen.push(`Vorbesitzer nicht beendet: ${endFehler.message}`);
  }

  try { await legeEigentuemerOrdnerAn(id, plan.objektId); }
  catch { warnungen.push('Drive-Ordner konnte nicht angelegt werden'); }

  // Fällig: sofort ausführen; sonst erledigt es der geplante Lauf am Stichtag
  if (wechselIds.length > 0 && e.beginn <= heute) {
    try { await fuehreWechselAus(wechselIds); }
    catch { warnungen.push('Ordnerwechsel wird nachgeholt'); }
  }
  return warnungen;
}
